import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserGroup, faPlus, faPenToSquare, faTrash, faXmark,
  faQrcode, faDownload, faRotateRight, faEye, faCheckCircle,
  faTimesCircle, faHistory
} from '@fortawesome/free-solid-svg-icons';
import NavigationUser from '../../Layouts/Navigation/NavigationUser';
import { BASE_URL } from '../../utils/constants';
import useAutoDismissSnack from '../../Hooks/AutoDismissSnack';

const BASE = `${BASE_URL}`;

const TIPO_LABELS = { FAMILIAR: 'Familiar', EMPLEADO: 'Empleado', OTRO: 'Otro' };
const TIPO_COLORS = {
  FAMILIAR: 'bg-blue-100 text-blue-800',
  EMPLEADO: 'bg-purple-100 text-purple-800',
  OTRO: 'bg-orange-100 text-orange-800',
};

const emptyForm = { nombre: '', apellido: '', email: '', telefono: '', identificacion: '', tipo: 'FAMILIAR', tipo_personalizado: '' };

function FormFields({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input className="input-field" required value={data.nombre}
            onChange={e => onChange({ ...data, nombre: e.target.value })} placeholder="Nombre" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
          <input className="input-field" required value={data.apellido}
            onChange={e => onChange({ ...data, apellido: e.target.value })} placeholder="Apellido" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Identificación</label>
          <input className="input-field" value={data.identificacion}
            onChange={e => onChange({ ...data, identificacion: e.target.value })} placeholder="Cédula / Pasaporte" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
          <input className="input-field" type="tel" value={data.telefono}
            onChange={e => onChange({ ...data, telefono: e.target.value })} placeholder="3001234567" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input className="input-field" type="email" value={data.email}
            onChange={e => onChange({ ...data, email: e.target.value })} placeholder="correo@ejemplo.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
          <select className="input-field" required value={data.tipo}
            onChange={e => onChange({ ...data, tipo: e.target.value })}>
            <option value="FAMILIAR">Familiar</option>
            <option value="EMPLEADO">Empleado</option>
            <option value="OTRO">Otro</option>
          </select>
        </div>
        {data.tipo === 'OTRO' && (
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción del tipo *</label>
            <input className="input-field" value={data.tipo_personalizado}
              onChange={e => onChange({ ...data, tipo_personalizado: e.target.value })}
              placeholder="Ej: Delivery habitual, Cuidador, etc." required />
          </div>
        )}
      </div>
    </div>
  );
}

function UserRecurringVisitors() {
  const [userId, setUserId] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const perPage = 8;

  // modals
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrVisitor, setQrVisitor] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyVisitor, setHistoryVisitor] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });
  const qrRef = useRef();

  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState('');
  const [snackMessage, setSnackMessage] = useState('');
  useAutoDismissSnack(openSnack, setOpenSnack);

  const snack = (status, msg) => { setSnackStatus(status); setSnackMessage(msg); setOpenSnack(true); };
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });
  const closeConfirm = () => setConfirmDialog({ open: false, message: '', onConfirm: null });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.id_usuario);
      } catch { }
    }
  }, []);

  useEffect(() => {
    if (userId) fetchVisitors();
  }, [userId]);

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE}/visitantesRecurrentes/user/${userId}`);
      setVisitors(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // CRUD handlers
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BASE}/visitantesRecurrentes`, { ...form, id_usuario: userId });
      snack('success', 'Visitante recurrente creado correctamente');
      setAddOpen(false);
      setForm(emptyForm);
      fetchVisitors();
    } catch (err) { snack('error', err.response?.data?.error || 'Error al crear visitante'); }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${BASE}/visitantesRecurrentes/${editItem.id}`, editItem);
      snack('success', 'Visitante actualizado correctamente');
      setEditOpen(false);
      fetchVisitors();
    } catch (err) { snack('error', err.response?.data?.error || 'Error al actualizar visitante'); }
  };

  const handleDelete = (v) => {
    openConfirm(`¿Eliminar a ${v.nombre} ${v.apellido} de visitantes recurrentes?`, async () => {
      closeConfirm();
      try {
        await axios.delete(`${BASE}/visitantesRecurrentes/${v.id}`);
        snack('success', 'Visitante eliminado correctamente');
        fetchVisitors();
      } catch (err) { snack('error', err.response?.data?.error || 'Error al eliminar visitante'); }
    });
  };

  const handleRegenerateQr = (v) => {
    openConfirm(`¿Regenerar el QR de ${v.nombre} ${v.apellido}? El QR anterior ya no funcionará.`, async () => {
      closeConfirm();
      try {
        const res = await axios.put(`${BASE}/visitantesRecurrentes/${v.id}/regenerarQr`);
        snack('success', 'QR regenerado correctamente');
        fetchVisitors();
        // If QR dialog open for this visitor, refresh token
        if (qrVisitor?.id === v.id) {
          setQrVisitor(prev => ({ ...prev, token_qr: res.data.token_qr }));
        }
      } catch (err) { snack('error', 'Error al regenerar QR'); }
    });
  };

  const handleDownloadQr = async () => {
    if (!qrRef.current) return;
    try {
      const canvas = await html2canvas(qrRef.current, { backgroundColor: '#fff', scale: 3 });
      const link = document.createElement('a');
      link.download = `QR-${qrVisitor.nombre}-${qrVisitor.apellido}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) { snack('error', 'Error al descargar QR'); }
  };

  const openHistoryFor = async (v) => {
    setHistoryVisitor(v);
    setHistoryOpen(true);
    try {
      const res = await axios.get(`${BASE}/visitantesRecurrentes/${v.id}/ingresos`);
      setHistoryList(res.data);
    } catch { setHistoryList([]); }
  };

  const paged = visitors.slice(page * perPage, (page + 1) * perPage);

  return (
    <>
      <NavigationUser />
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">

          {/* Snackbar */}
          {openSnack && (
            <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-lg shadow-lg ${snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
              <div className="flex items-center gap-2">
                <span>{snackMessage}</span>
                <button onClick={() => setOpenSnack(false)}><FontAwesomeIcon icon={faXmark} /></button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <FontAwesomeIcon icon={faUserGroup} className="h-10 w-10 text-indigo-600" />
              <h1 className="text-4xl font-bold text-gray-800">Visitantes Recurrentes</h1>
            </div>
            <p className="text-gray-500">Gestiona las personas que ingresan frecuentemente al conjunto</p>
          </div>

          {/* Add button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => { setForm(emptyForm); setAddOpen(true); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors shadow-sm"
            >
              <FontAwesomeIcon icon={faPlus} />
              Agregar Visitante
            </button>
          </div>

          {/* Cards grid */}
          {loading ? (
            <div className="text-center py-16 text-gray-400">Cargando...</div>
          ) : visitors.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faUserGroup} className="h-20 w-20 text-gray-200 mb-4" />
              <p className="text-xl text-gray-500">No tienes visitantes recurrentes registrados</p>
              <p className="text-sm text-gray-400 mt-2">Agrega familiares, empleados u otras personas frecuentes</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {paged.map(v => (
                  <div key={v.id} className="bg-white rounded-2xl shadow-md p-5 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{v.nombre} {v.apellido}</h3>
                        <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${TIPO_COLORS[v.tipo]}`}>
                          {v.tipo === 'OTRO' ? (v.tipo_personalizado || 'Otro') : TIPO_LABELS[v.tipo]}
                        </span>
                      </div>
                      <FontAwesomeIcon icon={faQrcode} className="h-8 w-8 text-indigo-300 shrink-0 mt-1" />
                    </div>
                    <div className="text-sm text-gray-600 space-y-0.5">
                      {v.identificacion && <div><span className="font-medium">ID:</span> {v.identificacion}</div>}
                      {v.telefono && <div><span className="font-medium">Tel:</span> {v.telefono}</div>}
                      {v.email && <div><span className="font-medium">Email:</span> {v.email}</div>}
                      <div><span className="font-medium">Ingresos:</span> {v.total_ingresos || 0}</div>
                      {v.ultimo_ingreso && (
                        <div><span className="font-medium">Último ingreso:</span> {new Date(v.ultimo_ingreso).toLocaleDateString('es-CO')}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100">
                      <button
                        onClick={() => { setQrVisitor(v); setQrOpen(true); }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
                      >
                        <FontAwesomeIcon icon={faQrcode} />
                        Ver QR
                      </button>
                      <button
                        onClick={() => openHistoryFor(v)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                      >
                        <FontAwesomeIcon icon={faHistory} />
                        Historial
                      </button>
                      <button
                        onClick={() => { setEditItem({ ...v }); setEditOpen(true); }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(v)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Pagination */}
              {visitors.length > perPage && (
                <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-5 py-3">
                  <span className="text-sm text-gray-600">{page * perPage + 1}–{Math.min((page + 1) * perPage, visitors.length)} de {visitors.length}</span>
                  <div className="flex gap-2">
                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                      className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 text-sm hover:bg-gray-50">Anterior</button>
                    <button disabled={(page + 1) * perPage >= visitors.length} onClick={() => setPage(p => p + 1)}
                      className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 text-sm hover:bg-gray-50">Siguiente</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal Agregar */}
      {addOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Agregar Visitante Recurrente</h2>
              <button onClick={() => setAddOpen(false)} className="text-white/80 hover:text-white"><FontAwesomeIcon icon={faXmark} className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="p-6"><FormFields data={form} onChange={setForm} /></div>
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                <button type="button" onClick={() => setAddOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold">Crear y Generar QR</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {editOpen && editItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Editar Visitante</h2>
              <button onClick={() => setEditOpen(false)} className="text-gray-400 hover:text-gray-600"><FontAwesomeIcon icon={faXmark} className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="p-6"><FormFields data={editItem} onChange={setEditItem} /></div>
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                <button type="button" onClick={() => setEditOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold">Guardar Cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal QR */}
      {qrOpen && qrVisitor && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
            <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">Código QR</h2>
              <button onClick={() => setQrOpen(false)} className="text-white/80 hover:text-white"><FontAwesomeIcon icon={faXmark} className="h-5 w-5" /></button>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <div ref={qrRef} className="p-4 bg-white rounded-xl border-2 border-indigo-100 shadow-inner flex flex-col items-center gap-2">
                <QRCodeSVG value={qrVisitor.token_qr} size={200} level="H" includeMargin={true} />
                <p className="text-sm font-bold text-gray-700 text-center">{qrVisitor.nombre} {qrVisitor.apellido}</p>
                <p className="text-xs text-gray-500 text-center">
                  {qrVisitor.tipo === 'OTRO' ? (qrVisitor.tipo_personalizado || 'Otro') : TIPO_LABELS[qrVisitor.tipo]}
                </p>
                <div className="mt-1 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs text-gray-500 text-center leading-none mb-0.5">Código de acceso</p>
                  <p className="text-xl font-black tracking-widest text-indigo-700 text-center">{qrVisitor.token_qr}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center">Este QR es permanente. Compártelo con el visitante para que lo presente al ingresar.</p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={handleDownloadQr}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold text-sm transition-colors"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Descargar QR
                </button>
                <button
                  onClick={() => handleRegenerateQr(qrVisitor)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold text-sm transition-colors"
                  title="Regenerar QR (el anterior dejará de funcionar)"
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                  Regenerar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historial */}
      {historyOpen && historyVisitor && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="bg-gray-800 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">Historial — {historyVisitor.nombre} {historyVisitor.apellido}</h2>
              <button onClick={() => setHistoryOpen(false)} className="text-white/80 hover:text-white"><FontAwesomeIcon icon={faXmark} className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              {historyList.length === 0 ? (
                <p className="text-center text-gray-400 py-8">Sin ingresos registrados</p>
              ) : (
                <ul className="space-y-2">
                  {historyList.map(h => (
                    <li key={h.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {new Date(h.fecha_ingreso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(h.fecha_ingreso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {h.observacion && <p className="text-xs text-gray-400 mt-0.5">{h.observacion}</p>}
                      </div>
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 h-5 w-5 shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button onClick={() => setHistoryOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />
                Confirmar acción
              </h2>
            </div>
            <div className="p-6 text-gray-700">{confirmDialog.message}</div>
            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t">
              <button onClick={closeConfirm} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
              <button onClick={confirmDialog.onConfirm} className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold">Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserRecurringVisitors;
